import { useAxios } from "@hooks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface ProctoringTerm {
  examTermId: string;
  examTitle: string;
  date: string;
  state: number;
  candidateCount: number;
  assignedToMeCount: number;
}

export interface ProctoringCandidate {
  examInstanceId: string;
  identity: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  examInstanceState: number;
  assessorId?: string | null;
  assessorName?: string | null;
}

export interface ProctoringSession {
  examTermId: string;
  examTitle: string;
  serverUrl: string;
  roomName: string;
  token: string;
  canManageAssignments: boolean;
  currentUserId: string;
  isAssessor: boolean;
  candidates: ProctoringCandidate[];
}

export interface ProctoringAssessor {
  assessorId: string;
  name: string;
  email?: string;
}

export interface ProctoringAssignments {
  assessors: ProctoringAssessor[];
  candidates: ProctoringCandidate[];
}

export interface ProctoringAssignmentItem {
  examInstanceId: string;
  assessorId: string | null;
}

export interface CandidateMediaCommand {
  examInstanceId: string;
  camera?: boolean;
  microphone?: boolean;
}

/// Asks a candidate's player to switch its camera/microphone; the result shows up as track mute events.
export const useSetCandidateMedia = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ examInstanceId, camera, microphone }: CandidateMediaCommand) => {
      await axios.post(`/api/proctoring/${examTermId}/candidates/${examInstanceId}/media`, { camera, microphone });
    },
  });
};

/// Open conversation through the LiveKit room metadata. Scope "mine" opens it only among the caller's own
/// candidates (always for assessors); "all" (admins) opens it for the whole room.
export const useSetOpenAudio = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ enabled, scope }: { enabled: boolean; scope: "mine" | "all" }) => {
      await axios.post(`/api/proctoring/${examTermId}/open-audio`, { enabled, scope });
    },
  });
};

/// Lets waiting candidates into the exam; without ids every waiting candidate the user may see.
export const useAdmitCandidates = (examTermId?: string) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (examInstanceIds?: string[]) =>
      (await axios.post<{ admitted: number }>(`/api/proctoring/${examTermId}/admit`, { examInstanceIds })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proctoringSession", examTermId] }),
  });
};

/// E-mails assessors a personal sign-in link to the proctoring wall.
export const useInviteAssessors = (examTermId?: string) => {
  const axios = useAxios();
  return useMutation({
    mutationFn: async ({ assessorIds, note }: { assessorIds?: string[]; note?: string }) =>
      (await axios.post<{ sent: string[]; failed: string[] }>(`/api/proctoring/${examTermId}/invite-assessors`, { assessorIds, note })).data,
  });
};

export const useGetProctoringTerms = () => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringTerms"],
    queryFn: async () => (await axios.get<ProctoringTerm[]>("/api/proctoring/terms")).data,
    refetchInterval: 30000,
  });
};

/// Candidates/assignments refresh periodically; the LiveKit token is only used on first connect.
export const useGetProctoringSession = (examTermId?: string) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringSession", examTermId],
    queryFn: async () => (await axios.get<ProctoringSession>(`/api/proctoring/${examTermId}/session`)).data,
    enabled: !!examTermId,
    refetchInterval: 30000,
    retry: 1,
  });
};

export const useGetProctoringAssignments = (examTermId: string, enabled: boolean) => {
  const axios = useAxios();
  return useQuery({
    queryKey: ["proctoringAssignments", examTermId],
    queryFn: async () => (await axios.get<ProctoringAssignments>(`/api/proctoring/${examTermId}/assignments`)).data,
    enabled,
  });
};

export const useSaveProctoringAssignments = (examTermId: string) => {
  const axios = useAxios();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (items: ProctoringAssignmentItem[]) => {
      await axios.put(`/api/proctoring/${examTermId}/assignments`, items);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proctoringAssignments", examTermId] });
      queryClient.invalidateQueries({ queryKey: ["proctoringSession", examTermId] });
    },
  });
};
